/**
 * Every webhook a table has, and the one being edited.
 *
 * One dialog rather than a page, because webhooks are set up once and then
 * looked at only when something has stopped working -- which is also why the
 * list leads with whether each one is still on. **The backend switches a
 * webhook off after enough failed calls**, and a webhook that quietly stopped
 * calling is the single thing somebody opens this to find out.
 */
import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { TableWebhook } from '@/api/types'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Skeleton } from '@/shared/ui/skeleton'
import { WebhookCalls } from './webhook-calls'
import { WebhookForm } from './webhook-form'
import { useWebhookMutations, useWebhooks, type WebhookDraft } from './queries'

/** A webhook that does not exist yet: everything, by POST, switched on. */
function blankDraft(): WebhookDraft {
  return {
    name: '',
    url: '',
    request_method: 'POST',
    include_all_events: true,
    events: [],
    headers: {},
    use_user_field_names: true,
    active: true,
  }
}

function draftFrom(webhook: TableWebhook): WebhookDraft {
  return {
    name: webhook.name,
    url: webhook.url,
    request_method: webhook.request_method ?? 'POST',
    include_all_events: webhook.include_all_events ?? true,
    // `events` arrives as an object keyed by event type rather than a list --
    // the serialiser reports what is stored, not what the form sends.
    events: Object.keys(webhook.events ?? {}) as WebhookDraft['events'],
    headers: (webhook.headers ?? {}) as Record<string, string>,
    use_user_field_names: webhook.use_user_field_names ?? true,
    active: webhook.active ?? true,
  }
}

export function WebhooksDialog({
  tableId,
  onClose,
}: {
  tableId: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const webhooks = useWebhooks(tableId)
  const { create, update, remove, isPending } = useWebhookMutations(tableId)

  /** Which webhook is open: its id, `'new'` for one being made, or nothing. */
  const [open, setOpen] = useState<number | 'new' | null>(null)
  const [draft, setDraft] = useState<WebhookDraft>(blankDraft)
  const [removing, setRemoving] = useState<TableWebhook | null>(null)

  const held = webhooks.data ?? []

  return (
    <>
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next) onClose()
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('webhook.title')}</DialogTitle>
            <DialogDescription>{t('webhook.description')}</DialogDescription>
          </DialogHeader>

          <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto">
            {webhooks.isPending ? (
              <Skeleton className="h-20" />
            ) : held.length === 0 && open !== 'new' ? (
              <p className="text-sm text-muted-foreground">{t('webhook.none')}</p>
            ) : (
              <ul aria-label={t('webhook.title')} className="flex flex-col gap-2">
                {held.map((webhook) => (
                  <li key={webhook.id} className="rounded border p-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() => {
                          const next = open === webhook.id ? null : webhook.id
                          setOpen(next)
                          if (next !== null) setDraft(draftFrom(webhook))
                        }}
                      >
                        <span className="block truncate text-sm font-medium">
                          {webhook.name}
                        </span>
                        <span className="block truncate font-mono text-xs text-muted-foreground">
                          {webhook.request_method} {webhook.url}
                        </span>
                      </button>
                      {/* Only when it is off. A webhook that is working needs no
                          badge; one the backend gave up on is the reason
                          somebody opened this. */}
                      {webhook.active === false && (
                        <span className="shrink-0 rounded bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                          {t('webhook.deactivated', {
                            count: webhook.failed_triggers ?? 0,
                          })}
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label={t('webhook.remove')}
                        onClick={() => {
                          setRemoving(webhook)
                        }}
                        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>

                    {open === webhook.id && (
                      <div className="mt-3 flex flex-col gap-4 border-t pt-3">
                        <WebhookForm
                          tableId={tableId}
                          draft={draft}
                          onChange={setDraft}
                        />
                        <WebhookCalls calls={webhook.calls} />
                        <div className="flex justify-end">
                          <Button
                            disabled={isPending}
                            onClick={() => {
                              update(webhook.id, draft)
                            }}
                          >
                            {t('webhook.save')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {open === 'new' && (
              <div className="flex flex-col gap-4 rounded border p-3">
                <WebhookForm tableId={tableId} draft={draft} onChange={setDraft} />
                <div className="flex justify-end">
                  <Button
                    disabled={
                      isPending || draft.name.trim() === '' || draft.url.trim() === ''
                    }
                    onClick={() => {
                      create(draft, () => {
                        setOpen(null)
                      })
                    }}
                  >
                    {t('webhook.save')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="border-t pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraft(blankDraft())
                setOpen('new')
              }}
            >
              <Plus className="size-4" />
              {t('webhook.add')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {removing !== null && (
        <ConfirmDialog
          open
          onOpenChange={() => {
            setRemoving(null)
          }}
          title={t('webhook.removeTitle', { name: removing.name })}
          description={t('webhook.removeDescription')}
          confirmLabel={t('action.delete')}
          destructive
          onConfirm={() => {
            remove(removing.id)
            setRemoving(null)
          }}
        />
      )}
    </>
  )
}
